import { FormControl, Grid, InputLabel, MenuItem, Select } from "@mui/material";
import { ReactElement, useEffect, useState } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { useFetchEdcSelectionItems } from "../../ExportDistributionCentres/Hooks/useFetchEdcSelectionItems";
import { BorderedFormGrid } from "../../Forms/BorderedFormGrid";
import { useResolveGeoCoordinates } from "../../GoogleMaps/useResolveGeoCoordinates";
import { Country } from "../../Locations/Country";
import { CountrySelect } from "../../Locations/CountrySelect";
import { nameOf } from "../../Utils/nameOf";
import Plant from "../Plant";
import { PlantType, plantTypeAsString } from "../PlantType";
import { PlantFormProps } from "./PlantFormProps";
import { GeoCoordinatesAddressTextField } from "../../Supplier/Form/GeoCoordinatesAddressTextField";
import { TextFieldInput } from "../../Forms/TextFieldInput";
import { isNotEmpty } from "../../Utils/strings";
import { RHFFieldErrorText } from "../../Forms/RHFFieldErrorText";
import { MdmFormProps } from "../../Forms/MdmFormProps";

export const PlantAddressForm = ({ plant, disabledFields }: PlantFormProps & MdmFormProps) => {
  const { control, setValue, getValues } = useFormContext<Plant>();
  const { data: selectableEDCs } = useFetchEdcSelectionItems();

  const getPlantTypeSelectionItems = (): ReactElement[] => {
    const plantTypeSelectionItems: ReactElement[] = [];
    plantTypeAsString.forEach((value, key) => {
      plantTypeSelectionItems.push(
        <MenuItem key={`plant-type-${value}`} value={key}>
          {value}
        </MenuItem>
      );
    });
    return plantTypeSelectionItems;
  };

  const [isAssignedEdcRequired, setIsAssignedEdcRequired] = useState(
    plant.PartDestination === PlantType.Ieo
  );
  useEffect(() => setIsAssignedEdcRequired(plant.PartDestination === PlantType.Ieo), [plant]);

  const updateIsoCode = (countryId: string | number, countries: Country[]) => {
    const selectedCountry = countries?.find((country) => country.Id === Number(countryId));
    setValue(nameOf<Plant>("CountryIsoCode"), selectedCountry?.IsoCode ?? "");
  };

  const onPartDestinationChanged = (selectedPartDestination: number) => {
    setIsAssignedEdcRequired(selectedPartDestination === PlantType.Ieo);
    if (selectedPartDestination !== PlantType.Ieo) setValue(nameOf<Plant>("EdcId"), 0);
  };

  const { resolveGeoCoordinatesIfAddressIsComplete } = useResolveGeoCoordinates<Plant>(
    "Street",
    "ZipCode",
    "City",
    "Latitude",
    "Longitude"
  );

  return (
    <BorderedFormGrid>
      {/* FIRST ROW */}
      <Grid item xs={4}>
        <TextFieldInput name="Name" control={control} required disabledFields={disabledFields} />
      </Grid>
      <Grid item xs={4}>
        <TextFieldInput name="Region" control={control} disabledFields={disabledFields} />
      </Grid>
      <Grid item xs={4} />

      {/* SECOND ROW */}
      <Grid item xs={4}>
        <GeoCoordinatesAddressTextField
          name="Street"
          control={control}
          disabledFields={disabledFields}
          triggerGeoCoordinatesCalculation={(fallbackGeoCoordinates) => {
            setValue("Latitude", fallbackGeoCoordinates.latitude);
            setValue("Longitude", fallbackGeoCoordinates.longitude);
            if (isNotEmpty(getValues("Street"))) resolveGeoCoordinatesIfAddressIsComplete();
          }}
        />
      </Grid>
      <Grid item xs={4}>
        <CountrySelect
          control={control}
          name="CountryId"
          onCountryChanged={updateIsoCode}
          required
        />
      </Grid>
      <Grid item xs={4}>
        <TextFieldInput
          name="CountryIsoCode"
          control={control}
          label="ISO Country Code"
          disabledFields={disabledFields}
        />
      </Grid>

      {/* THIRD ROW */}
      <Grid item xs={4}>
        <GeoCoordinatesAddressTextField
          name="ZipCode"
          control={control}
          label="Postcode"
          disabledFields={disabledFields}
          triggerGeoCoordinatesCalculation={(fallbackGeoCoordinates) => {
            setValue("Latitude", fallbackGeoCoordinates.latitude);
            setValue("Longitude", fallbackGeoCoordinates.longitude);
            if (isNotEmpty(getValues("ZipCode"))) resolveGeoCoordinatesIfAddressIsComplete();
          }}
        />
      </Grid>
      <Grid item xs={4}>
        <Controller
          control={control}
          name={nameOf<Plant>("PartDestination")}
          render={({ field, fieldState: { error } }) => (
            <FormControl fullWidth size="small" error={!!error}>
              <InputLabel id="part-destination-label" required>
                Part Destination
              </InputLabel>
              <Select
                {...field}
                labelId="part-destination-label"
                label="Part Destination"
                required
                error={!!error}
                onChange={(event) => {
                  onPartDestinationChanged(event.target.value as number);
                  field.onChange(event);
                }}
              >
                {getPlantTypeSelectionItems()}
              </Select>
              {!!error && <RHFFieldErrorText fieldError={error} />}
            </FormControl>
          )}
        />
      </Grid>

      <Grid item xs={4}>
        <Controller
          name={nameOf<Plant>("EdcId")}
          control={control}
          render={({ field, fieldState: { error } }) => (
            <FormControl
              fullWidth
              size="small"
              error={!!error}
              disabled={!isAssignedEdcRequired}
              required={isAssignedEdcRequired}
            >
              <InputLabel id="assigned-edc-label">Assigned EDC</InputLabel>
              <Select {...field} labelId="assigned-edc-label" label="Assigned EDC" error={!!error}>
                <MenuItem value={0}>-</MenuItem>
                {selectableEDCs &&
                  selectableEDCs.map((edc) => (
                    <MenuItem key={`${edc.Id}-${edc.Gsdb}`} value={edc.Id}>
                      {`${edc.Gsdb} | ${edc.Name}`}
                    </MenuItem>
                  ))}
              </Select>
              {!!error && <RHFFieldErrorText fieldError={error} />}
            </FormControl>
          )}
        />
      </Grid>

      {/* FOURTH ROW */}
      <Grid item xs={4}>
        <TextFieldInput
          name="ZipArea"
          control={control}
          label="Postcode Area"
          disabledFields={disabledFields}
        />
      </Grid>
      <Grid item xs={4}>
        <TextFieldInput
          name="PlantDetails"
          control={control}
          label="Plant Details"
          disabledFields={disabledFields}
        />
      </Grid>
      <Grid item xs={4} />

      {/* FIFTH ROW */}
      <Grid item xs={4}>
        <GeoCoordinatesAddressTextField
          name="City"
          control={control}
          disabledFields={disabledFields}
          triggerGeoCoordinatesCalculation={(fallbackGeoCoordinates) => {
            setValue("Latitude", fallbackGeoCoordinates.latitude);
            setValue("Longitude", fallbackGeoCoordinates.longitude);
            if (isNotEmpty(getValues("City"))) resolveGeoCoordinatesIfAddressIsComplete();
          }}
        />
      </Grid>
      <Grid item xs={8} />
    </BorderedFormGrid>
  );
};
